import React, { useState } from 'react';
import {
  HeaderBar,
  EnrolButton,
  BusPaperWrapper,
  ActionsContainer,
  RefreshButton,
  Title
} from './styled';
import EnrolBusDialog from './components/EnrolBusDialog';
import EditBusDialog from './components/EditBusDialog';
import { Bus, BusAction } from './types/busTypes';
import BusTable from './components/BusTable';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import Icons from '../../components/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import DeleteOrUnlinkBusDialog from './components/DeleteOrUnlinkBusDialog';
import GenerateQrDialog from './components/GenerateQrDialog';
import useBuses from './hooks/useBuses';

const BusManagementPage: React.FC = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const schoolId = loggedInUser?.school;
  const {
    buses: rows,
    loading,
    refresh: refreshBuses
  } = useBuses({
    schoolId,
    onError: () => showToast('Failed to load buses', SnackbarSeverityType.error)
  });
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [busBeingEdited, setBusBeingEdited] = useState<Bus | null>(null);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    action: BusAction | null;
    bus: Bus | null;
  }>({ open: false, action: null, bus: null });
  const [qrDialog, setQrDialog] = useState<{ open: boolean; bus: Bus | null }>({
    open: false,
    bus: null
  });
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: SnackbarSeverityType.success
  });

  const showToast = (message: string, severity: SnackbarSeverityType) =>
    setToast({ open: true, message, severity });

  const openEnrolDialog = () => setEnrolOpen(true);
  const closeEnrolDialog = () => setEnrolOpen(false);

  const openEditDialog = (bus: Bus) => setBusBeingEdited(bus);
  const closeEditDialog = () => setBusBeingEdited(null);

  const requestDeleteBus = (bus: Bus) =>
    setConfirm({ open: true, action: BusAction.delete, bus });
  const requestUnlinkBus = (bus: Bus) =>
    setConfirm({ open: true, action: BusAction.unlink, bus });

  const handleGenerateQr = (bus: Bus) => {
    setQrDialog({ open: true, bus });
  };

  const handleCloseQrDialog = () => {
    setQrDialog({ open: false, bus: null });
  };

  const handleConfirmCompleted = () => {
    setConfirm({ open: false, action: null, bus: null });
    refreshBuses();
  };

  const handleCancelConfirm = () => {
    setConfirm({ open: false, action: null, bus: null });
  };

  return (
    <>
      <HeaderBar>
        <Title sx={{fontSize: '24px !important'}}>Bus Management</Title>
        <ActionsContainer>
          <RefreshButton variant='outlined' onClick={refreshBuses}>
            Refresh
          </RefreshButton>
          <EnrolButton
            variant='contained'
            onClick={openEnrolDialog}
            startIcon={<Icons.Bus />}
          >
            Enrol A Bus
          </EnrolButton>
        </ActionsContainer>
      </HeaderBar>
      <BusPaperWrapper>
        <BusTable
          rows={rows}
          loading={loading}
          onUnlink={requestUnlinkBus}
          onEdit={openEditDialog}
          onDelete={requestDeleteBus}
          onGenerateQr={handleGenerateQr}
        />
        <EditBusDialog
          open={Boolean(busBeingEdited)}
          bus={busBeingEdited}
          onClose={closeEditDialog}
          onSuccess={refreshBuses}
          showToast={showToast}
        />
      </BusPaperWrapper>
      <EnrolBusDialog
        open={enrolOpen}
        schoolId={schoolId}
        onClose={closeEnrolDialog}
        onSuccess={refreshBuses}
        showToast={showToast}
      />
      <DeleteOrUnlinkBusDialog
        open={confirm.open}
        action={confirm.action}
        bus={confirm.bus}
        onCancel={handleCancelConfirm}
        onCompleted={handleConfirmCompleted}
        showToast={showToast}
      />
      <GenerateQrDialog
        open={qrDialog.open}
        bus={qrDialog.bus}
        onClose={handleCloseQrDialog}
        showToast={showToast}
      />
      <ToastMessage
        {...toast}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default BusManagementPage;
